import { Divider } from '@design-system';
import { FC, useEffect, useMemo } from 'react';

import { JOURNALS_FRANCHISE_INDEX_PAGE_TYPE } from '../../constants';
import { AuthEntryPoint } from '../../features/auth/types';
import { FranchiseIndex, Franchises } from '../../types/Franchises';
import { TrackPageView } from '../../utils/analytics';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { getAnalyticsMap } from '../../utils/journals/franchises';
import PageHeading from '../Layout/PageHeading';
import PageSection from '../Layout/PageSection';
import { SubscriptionBanner } from '../Subscriptions';
import { JournalsBreadcrumbs } from './JournalsBreadcrumbs';
import { JournalsGrid } from './JournalsGrid';

type FranchisePageProps = {
  franchise: string;
  franchiseIndex: FranchiseIndex;
  locale: string;
  pagination: {
    page: number;
    perPage: number;
    total: number;
  };
  stories: Franchises[];
};

export const FranchisePage: FC<FranchisePageProps> = ({
  franchise,
  franchiseIndex,
  locale,
  pagination,
  stories,
}) => {
  const { subtitle, title } = franchiseIndex.content;
  const [subscription] = franchiseIndex.content.subscription;

  const properties = useMemo(
    () => ({
      'articles count': stories.length,
      locale,
      franchise,
    }),
    [locale, franchise, stories.length],
  );

  const [analyticsEmitter] = useAnalyticsEmitter(properties);

  useEffect(() => {
    TrackPageView({
      page: JOURNALS_FRANCHISE_INDEX_PAGE_TYPE,
      title,
      properties,
    });
  }, [locale, title, properties]);

  return (
    <>
      <JournalsBreadcrumbs
        basePath={{
          href: `/insights/${franchise}`,
          name: title,
        }}
        handleClick={(item) => {
          analyticsEmitter('journals franchise', 'breadcrumb click', { item });
        }}
      />

      <main className="my-9 space-y-9 md:my-12 md:space-y-20">
        <PageSection>
          <PageHeading heading={title} strapline={subtitle} />
        </PageSection>

        <PageSection>
          <JournalsGrid
            franchise={franchise}
            handleClick={(story, index) => {
              analyticsEmitter('journals franchise', 'article click', {
                position: index + 1,
                ...getAnalyticsMap(locale, franchise, story),
              });
            }}
            handlePaginationClick={(page: number) => {
              analyticsEmitter('journals franchise', 'pagination click', {
                nextPage: page,
                pagination,
              });
            }}
            pagination={pagination}
            stories={stories}
          />
        </PageSection>

        <div className="wrapper-website">
          <Divider className="my-9 md:my-12" />
        </div>

        <PageSection>
          <SubscriptionBanner
            buttonLocation="insights franchise index"
            image={subscription.image.filename}
            source={AuthEntryPoint.NewsletterInsights}
            subtitle={subscription.subtitle}
            title={subscription.title}
          />
        </PageSection>
      </main>
    </>
  );
};
